import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import { Calendar, CheckCircle, XCircle, Clock, MapPin, RefreshCw, User, Plus, X, Search, Printer } from "lucide-react";
import { getItems } from "@/utils/apiHelpers";

const STATUS_CONFIG = {
  PROPOSED: { color: "bg-amber-100 text-amber-700", icon: Clock, label: "En attente" },
  CONFIRMED: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle, label: "Confirmé" },
  CANCELLED: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Annulé" },
  COMPLETED: { color: "bg-slate-100 text-slate-600", icon: CheckCircle, label: "Terminé" },
};

export default function NurseAppointments() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("PROPOSED");

  // Confirm modal
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [confirmDate, setConfirmDate] = useState("");
  const [confirmLocation, setConfirmLocation] = useState("");
  const [confirmNotes, setConfirmNotes] = useState("");

  // New appointment modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [newAppt, setNewAppt] = useState({
    patientId: "",
    destination: "", // "doctor:ID" or "service:TYPE"
    notes: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [printPatientFilter, setPrintPatientFilter] = useState("");

  useEffect(() => {
    fetchAppointments();
    fetchPatientsAndDoctors();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const res = await api.get("/appointments");
      setAppointments(res.data);
    } catch {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientsAndDoctors = async () => {
    try {
      const [patientsRes, recipientsRes] = await Promise.all([
        api.get("/patients?limit=500").catch(() => ({ data: { items: [] } })),
        api.get("/threads/recipients").catch(() => ({ data: { clinicians: [] } })),
      ]);
      setPatients(getItems(patientsRes.data));
      // Filter only doctors from clinicians list
      const doctorsList = recipientsRes.data.clinicians?.filter(c => c.role === "MEDECIN") || [];
      setDoctors(doctorsList);
    } catch {
      // Silent fail
    }
  };

  const confirmAppointment = async () => {
    if (!confirmDate) {
      toast.error("Veuillez sélectionner une date");
      return;
    }
    try {
      await api.patch(`/appointments/${showConfirmModal}/confirm`, {
        date: confirmDate,
        location: confirmLocation,
        notes: confirmNotes,
      });
      toast.success("Rendez-vous confirmé");
      setShowConfirmModal(null);
      setConfirmDate("");
      setConfirmLocation("");
      setConfirmNotes("");
      fetchAppointments();
    } catch {
      toast.error("Erreur");
    }
  };

  const cancelAppointment = async (id) => {
    if (!confirm("Annuler ce rendez-vous ?")) return;
    try {
      await api.patch(`/appointments/${id}/cancel`);
      toast.success("Rendez-vous annulé");
      fetchAppointments();
    } catch {
      toast.error("Erreur");
    }
  };

  const completeAppointment = async (id) => {
    try {
      await api.patch(`/appointments/${id}/complete`);
      toast.success("Rendez-vous marqué comme terminé");
      fetchAppointments();
    } catch {
      toast.error("Erreur");
    }
  };

  const SERVICE_DESTINATIONS = [
    { value: "service:Hôpital de jour", label: "Hôpital de jour" },
    { value: "service:Radiothérapie", label: "Radiothérapie" },
    { value: "service:Laboratoire", label: "Laboratoire" },
    { value: "service:Imagerie", label: "Imagerie" },
    { value: "service:Bilan", label: "Bilan" },
    { value: "service:Urgence", label: "Urgence" },
  ];

  const createAppointment = async () => {
    if (!newAppt.patientId) {
      toast.error("Veuillez sélectionner un patient");
      return;
    }
    if (!newAppt.destination) {
      toast.error("Veuillez sélectionner une destination");
      return;
    }

    // Parse destination: "doctor:ID" or "service:TYPE"
    const [destType, destValue] = newAppt.destination.split(":");
    const payload = {
      patientId: newAppt.patientId,
      doctorId: destType === "doctor" ? destValue : null,
      type: destType === "doctor" ? "Consultation" : destValue,
      notes: newAppt.notes || null,
    };

    setCreating(true);
    try {
      await api.post("/appointments", payload);
      toast.success("Demande de rendez-vous créée");
      setShowNewModal(false);
      setNewAppt({ patientId: "", destination: "", notes: "" });
      setSearchQuery("");
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const printAppointments = () => {
    let toPrint = appointments.filter(a => a.status === "CONFIRMED");
    if (printPatientFilter) {
      toPrint = toPrint.filter(a => a.patientId === printPatientFilter);
    }
    if (toPrint.length === 0) {
      toast.error("Aucun rendez-vous confirmé à imprimer");
      return;
    }
    const patientLabel = printPatientFilter
      ? (() => { const p = patients.find(pp => pp.id === printPatientFilter); return p ? `${p.firstName} ${p.lastName}` : ""; })()
      : "Tous les patients";
    const printWindow = window.open("", "_blank");
    const rows = toPrint.map(a => {
      const p = patients.find(pp => pp.id === a.patientId);
      const name = p ? `${p.firstName} ${p.lastName}` : a.patientId;
      const dest = a.doctorName || a.type || "-";
      const date = a.date ? new Date(a.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
      return `<tr><td>${name}</td><td>${dest}</td><td>${date}</td><td>${a.location || "-"}</td><td>${a.notes || "-"}</td></tr>`;
    }).join("");
    printWindow.document.write(`
      <html><head><title>Rendez-vous - Hana</title>
      <style>body{font-family:Arial,sans-serif;padding:20px}h1{font-size:18px;color:#333}table{width:100%;border-collapse:collapse;margin-top:16px}th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:13px}th{background:#f5f5f5;font-weight:600}@media print{body{padding:0}}</style>
      </head><body>
      <h1>Liste des rendez-vous confirmés</h1>
      <p style="color:#666;font-size:12px">Patient : ${patientLabel} | Imprimé le ${new Date().toLocaleDateString("fr-FR")} | ${toPrint.length} rendez-vous</p>
      <table><thead><tr><th>Patient</th><th>Destination</th><th>Date</th><th>Lieu</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredAppointments = appointments.filter(a => 
    filter === "all" ? true : a.status === filter
  );

  const filteredPatients = patients.filter(p => 
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Calendar className="w-7 h-7 text-emerald-600" />
              Rendez-vous
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {appointments.filter(a => a.status === "PROPOSED").length} demandes en attente
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <select
              value={printPatientFilter}
              onChange={e => setPrintPatientFilter(e.target.value)}
              data-testid="print-patient-filter"
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600"
            >
              <option value="">Tous les patients</option>
              {patients.map(p => (
                <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
              ))}
            </select>
            <button
              onClick={printAppointments}
              data-testid="print-appointments-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200"
            >
              <Printer className="w-4 h-4" />
              Imprimer
            </button>
            <button
              onClick={() => setShowNewModal(true)}
              data-testid="new-appointment-btn"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Demander un RDV
            </button>
            <button
              onClick={fetchAppointments}
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {[
            { value: "PROPOSED", label: "En attente" },
            { value: "CONFIRMED", label: "Confirmés" },
            { value: "COMPLETED", label: "Terminés" },
            { value: "all", label: "Tous" },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                filter === f.value
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Appointments list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucun rendez-vous</p>
          </div>
        ) : (
          <div className="dash-card p-0 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Patient</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Destination</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Statut</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Notes</th>
                  <th className="p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAppointments.map(appt => {
                  const config = STATUS_CONFIG[appt.status] || STATUS_CONFIG.PROPOSED;
                  const Icon = config.icon;
                  return (
                    <tr key={appt.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="font-medium text-slate-800">{appt.patientName || "Patient"}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm font-medium text-slate-700">{appt.doctorName || appt.type || "—"}</span>
                      </td>
                      <td className="p-4">
                        {appt.date ? (
                          <span className="text-sm text-slate-700">
                            {new Date(appt.date).toLocaleDateString("fr-FR", {
                              day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
                            })}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400 italic">À définir</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${config.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                          {config.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-500 max-w-[200px] truncate block">{appt.notes || "—"}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {appt.status === "PROPOSED" && (
                            <>
                              <button
                                onClick={() => setShowConfirmModal(appt.id)}
                                className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium hover:bg-emerald-100"
                              >
                                Confirmer
                              </button>
                              <button
                                onClick={() => cancelAppointment(appt.id)}
                                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100"
                              >
                                Annuler
                              </button>
                            </>
                          )}
                          {appt.status === "CONFIRMED" && (
                            <button
                              onClick={() => completeAppointment(appt.id)}
                              className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium hover:bg-emerald-100"
                            >
                              Terminer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b bg-emerald-50">
              <h2 className="text-lg font-bold text-slate-800">Confirmer le rendez-vous</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date et heure *</label>
                <input
                  type="datetime-local"
                  value={confirmDate}
                  onChange={e => setConfirmDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Lieu</label>
                <input
                  type="text"
                  value={confirmLocation}
                  onChange={e => setConfirmLocation(e.target.value)}
                  placeholder="Cabinet, Hôpital..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={confirmNotes}
                  onChange={e => setConfirmNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowConfirmModal(null)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmAppointment}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Appointment Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="new-appointment-modal">
            <div className="p-6 border-b bg-emerald-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  Demander un rendez-vous
                </h2>
                <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-white/50 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Patient Search */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Patient *</label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un patient..."
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm"
                    data-testid="patient-search"
                  />
                </div>
                <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-xl">
                  {filteredPatients.slice(0, 10).map(p => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setNewAppt(prev => ({ ...prev, patientId: p.id, doctorId: p.doctorId || "" }));
                        setSearchQuery(`${p.firstName} ${p.lastName}`);
                      }}
                      className={`w-full p-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center gap-3 ${
                        newAppt.patientId === p.id ? "bg-emerald-50" : ""
                      }`}
                      data-testid={`patient-option-${p.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-800">{p.firstName} {p.lastName}</p>
                        <p className="text-xs text-slate-500">{p.pathology || "—"}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Destination (unified doctor/service selector) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Destination *</label>
                <select
                  value={newAppt.destination}
                  onChange={e => setNewAppt(prev => ({ ...prev, destination: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm"
                  data-testid="destination-select"
                >
                  <option value="">Sélectionner une destination...</option>
                  <optgroup label="Médecins">
                    {doctors.map(d => (
                      <option key={d.id} value={`doctor:${d.id}`}>
                        {d.name || `Dr. ${d.firstName} ${d.lastName}`}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Services">
                    {SERVICE_DESTINATIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optionnel)</label>
                <textarea
                  value={newAppt.notes}
                  onChange={e => setNewAppt(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Raison du rendez-vous..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={createAppointment}
                  disabled={!newAppt.patientId || !newAppt.destination || creating}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="create-appointment-btn"
                >
                  {creating ? "..." : "Demander"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
}
